import React, { useContext, useEffect, useState } from "react";
import { Modal, Box, Typography, CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { doc, updateDoc, setDoc, getDoc } from "firebase/firestore";
import { db } from "../@shared/firebase/config";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { Close } from "@mui/icons-material";
import { MainContext } from "../App";
import NeoButton from "./NeoButton";
import { useCardStore } from "../@shared/store/useCardsStore";
import PackOpeningModal from "./PackOpeningModal";
import CardRewardModal from "./CardRewardModal";
import PurchasedItemModal from "./PurchasedItemModal";

const backdropStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "rgba(0,0,0,0.6)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1400,
};

const modalWrapperStyle = {
  width: "90vw",
  maxWidth: "500px",
  aspectRatio: 4 / 5,
  borderRadius: "16px",
  position: "relative",
};

const modalContentStyle = {
  width: "85%",
  height: "85%",
  display: "flex",
  flexDirection: "column",
  padding: "5% 8% 12%",
  backgroundColor: "#2e1a47",
  position: "relative",
};

const scrollAreaStyle = {
  marginTop: "10px",
  overflowY: "auto",
  maxHeight: "80%",
};

const purchaseRowStyle = {
  marginBottom: "12px",
  backgroundColor: "#452098",
  padding: "12px",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
};

export default function MyPurchasesModal({ visible, onClose }) {
  const { userData } = useAuthStore();
  const { infinityCards, characterCards } = useCardStore();
  const { Theme } = useContext(MainContext);
  const [loading, setLoading] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [claiming, setClaiming] = useState(null);
  const [purchasedCard, setPurchasedCard] = useState(null);
  const [packModalVisible, setPackModalVisible] = useState(false);
  const [cardModalVisible, setCardModalVisible] = useState(false);
  const [purchasedItemModalVisible, setPurchasedItemModalVisible] =
    useState(false);
  const [purchasedItem, setPurchasedItem] = useState(null);

  useEffect(() => {
    if (visible) {
      setLoading(true);
      const sorted = [...(userData?.purchases || [])].sort((x) =>
        x.claimed ? 1 : -1
      );
      setPurchases(sorted);
      setTimeout(() => setLoading(false), 600);
    }
  }, [visible, userData?.purchases]);

  const createPack = (cardString) => {
    if (!cardString) return [];
    if (Array.isArray(cardString)) return cardString;

    console.log("cardString", cardString);
    const entries = cardString.split(",").map((entry) => entry.trim());

    const cards = entries.map((entry) => {
      const [typeRaw, cardId] = entry.split("-");
      const type = typeRaw.toLowerCase();

      if (type === "infinity") {
        return {
          ...infinityCards.find((card) => card.id === cardId),
          type: "Infinity",
        };
      } else if (type === "character" || type === "chacracter") {
        return {
          ...characterCards.find((card) => card.id === cardId),
          type: "character",
        };
      } else {
        console.warn("Unknown card type:", entry);
        return null;
      }
    });

    return cards.filter(Boolean); // removes null entries
  };

  const handleClaimPurchase = (purchase) => {
    setClaiming(purchase);
    let purchaseCopy = { ...purchase };
    if (purchaseCopy.productType.toLowerCase().includes("pack")) {
      purchaseCopy.product.pack = createPack(purchase.product.cards);
    }
    console.log("Claiming purchase:", purchaseCopy);

    switch (purchase.productType.toLowerCase()) {
      case "pack":
      case "card pack":
      case "elemental pack":
      case "collection pack":
        setPackModalVisible(true);
        break;
      case "character card":
      case "finite card":
      case "infinity card":
        setPurchasedCard({ ...purchase, reward: purchase.product });
        setCardModalVisible(true);
        break;
      case "avatars":
      case "skins":
      case "emotes":
      case "stickers":
      case "screens":
      case "graveyards":
        setPurchasedItemModalVisible(true);
        setPurchasedItem(purchase);
        break;

      default:
        console.warn("Unknown reward type");
    }
  };

  const finalizeClaim = async (item) => {
    try {
      const userId = userData.id;
      const itemId = item.id;
      const purchaseRef = doc(db, "users", userId, "purchases", itemId);

      await updateDoc(purchaseRef, {
        claimed: true,
        claimedAt: Date.now(),
      });

      let targetCollection;

      switch (item.productType.toLowerCase()) {
        case "character card":
          targetCollection = "Character cards";
          break;
        case "infinity card":
          targetCollection = "Infinity cards";
          break;
        case "avatars":
          targetCollection = "avatars";
          break;
        case "skins":
          targetCollection = "skins";
          break;
        case "emotes":
          targetCollection = "emotes";
          break;
        case "stickers":
          targetCollection = "stickers";
          break;
        case "screens":
          targetCollection = "screens";
          break;
        case "graveyards":
          targetCollection = "graveyards";
          break;
        default:
          break;
      }

      if (targetCollection) {
        const itemRef = doc(
          db,
          "users",
          userId,
          targetCollection,
          item?.product?.id
        );
        const snap = await getDoc(itemRef);

        if (snap.exists()) {
          await updateDoc(itemRef, {
            amountOwned: (snap.data().amountOwned || 0) + (item.amount || 1),
            new: true,
          });
        } else {
          await setDoc(itemRef, {
            ...item.product,
            amountOwned: item.amount || 1,
            createdAt: Date.now(),
            lastUpdated: Date.now(),
            new: true,
          });
        }
      }

      setClaiming(null);
    } catch (err) {
      console.error("Error claiming item:", err);
    }
  };

  const renderPurchase = (p) => (
    <Box key={p.id} sx={purchaseRowStyle}>
      <Typography color="#fff" fontWeight={600} sx={{ flex: 1 }}>
        {p?.product?.name}
        <br />
        <span style={{ fontSize: 12 }}>{p?.type || p?.productType}</span>
      </Typography>
      {p.claimed ? (
        <Typography color="#0f0">Claimed</Typography>
      ) : (
        <NeoButton
          text="Claim"
          width={30}
          color="$primary"
          onClick={() => handleClaimPurchase(p)}
        />
      )}
    </Box>
  );

  return (
    <>
      <Modal open={visible} onClose={onClose} closeAfterTransition>
        <AnimatePresence>
          {visible && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={backdropStyle}
            >
              <motion.div
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -50, opacity: 0 }}
                transition={{ duration: 0.3 }}
                style={modalWrapperStyle}
              >
                <Box sx={modalContentStyle}>
                  <Typography
                    color="#fff"
                    fontWeight="bold"
                    variant="h5"
                    align="center"
                    sx={{ mt: 1, mb: 1 }}
                  >
                    My Purchases
                  </Typography>
                  {loading ? (
                    <Box
                      sx={{
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                      }}
                    >
                      <CircularProgress color="inherit" />
                      <Typography color="#fff" sx={{ mt: 1 }}>
                        Loading...
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        ...scrollAreaStyle,
                        maxHeight: { md: "85%" },
                        scrollbarWidth: "none",
                      }}
                    >
                      {purchases.map(renderPurchase)}
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: "absolute",
                      bottom: "-2%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <NeoButton
                      isIcon={true}
                      icon={<Close />}
                      color={Theme.palette.secondary.main}
                      onClick={onClose}
                    />
                  </Box>
                </Box>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </Modal>

      {/* Nested Modals */}
      <PackOpeningModal
        visible={packModalVisible}
        onClose={() => {
          setPackModalVisible(false);
          finalizeClaim(claiming);
        }}
        reward={
          Array.isArray(claiming?.reward?.pack)
            ? claiming?.reward?.pack
            : claiming?.reward?.pack?.cards
        }
      />

      {purchasedCard && (
        <CardRewardModal
          visible={cardModalVisible}
          reward={purchasedCard}
          onClose={() => {
            setCardModalVisible(false);
            finalizeClaim(purchasedCard);
            setPurchasedCard(null);
          }}
        />
      )}

      <PurchasedItemModal
        visible={purchasedItemModalVisible}
        item={purchasedItem}
        onClose={() => {
          setPurchasedItemModalVisible(false);
          finalizeClaim(purchasedItem);
          setPurchasedItem(null);
        }}
      />
    </>
  );
}
